import {
  Avatar,
  Block,
  Box,
  Icon,
  IconWithTextLockup,
  NavList,
} from '@deckee/deck-hand';
import React, { FC, MouseEvent, ReactNode } from 'react';
import styled, { css, useTheme } from 'styled-components';
import { getStoredImage, getWatercraftImage } from '../../../../services/image';
import { Vessel } from '../../../../services/store/modules/vessels/models';
import {
  getWatercraftName,
  getWatercraftTypeLengthLabel,
} from '../utils/watercraft-display';

const IconWithTextLockupListItem = styled(IconWithTextLockup).attrs({})`
  overflow: visible !important;
  // Last child has no margin bottom
  &:last-child {
    margin-bottom: 0;
  }
`;

const VesselNavListItem = styled(NavList.Item)<{ error?: boolean }>`
  ${({ error, theme }) =>
    error &&
    css`
      padding-left: 4px;
      margin-left: -4px;
      border: 2px solid ${theme.colors.danger};
      border-radius: 5px;
    `}
`;

interface VesselListItemProps {
  vessel: Vessel;
  onClick?: () => void;
  // When editable, the avatar shows a pencil indicator that triggers onEdit.
  editable?: boolean;
  onEdit?: () => void;
  // Optional extra content rendered below the standard two lines (e.g. a
  // "more details needed" warning).
  note?: ReactNode;
  error?: boolean;
  bg?: string;
}

const VesselListItem: FC<VesselListItemProps> = ({
  vessel,
  onClick = undefined,
  editable = false,
  onEdit = undefined,
  note = undefined,
  error = false,
  bg = undefined,
}) => {
  const theme = useTheme();

  const handleEdit = (event: MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    onEdit?.();
  };

  const baseImage = vessel.image ? (
    <Avatar src={getStoredImage(vessel.image, 40)} />
  ) : (
    getWatercraftImage(vessel.watercraft_type, 40)
  );

  const editIndicator = (
    <Avatar.Indicator
      bg="white"
      border="1px solid"
      borderColor={theme.colors.success}
      onClick={handleEdit}
    >
      <Icon name="pencil" fill={theme.colors.success} height={12} width={16} />
    </Avatar.Indicator>
  );

  const image = editable ? (
    <Box onClick={handleEdit}>
      {vessel.image ? (
        <Avatar src={getStoredImage(vessel.image, 40)}>{editIndicator}</Avatar>
      ) : (
        <Block position="relative">
          {getWatercraftImage(vessel.watercraft_type, 40)}
          {editIndicator}
        </Block>
      )}
    </Box>
  ) : (
    baseImage
  );

  return (
    <VesselNavListItem chevron ellipsis error={error} onClick={onClick} bg={bg}>
      <IconWithTextLockupListItem
        image={image}
        emphasis="heading"
        heading={getWatercraftName(vessel)}
        description={getWatercraftTypeLengthLabel(vessel)}
        useEllipsis
      >
        {note}
      </IconWithTextLockupListItem>
    </VesselNavListItem>
  );
};

export default VesselListItem;
